import { useEffect, useMemo, useState } from "react";
import { Loader2, Sparkles, AlertTriangle, Search, ChevronLeft, ChevronRight } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import ExpandableText from "@/components/ui/ExpandableText";
import { Badge } from "@/components/ui/Badge";
import { useApiData } from "@/hooks/useApiData";
import { getUpdates } from "@/api/referrals";
import { formatDate } from "@/lib/dates";
import { pageList } from "@/lib/pagination";
import { cn } from "@/lib/cn";
import { categoryTone, audienceLabel } from "@/lib/changelog";

// NO demo fallback. This page used to fall back to two invented changelog entries
// ("v1.5 AI Providers & fallback", "v1.4 Integrations") whenever the backend was
// unreachable, rendered identically to real published release notes — the page
// discarded the hook's `demo` flag, so nothing marked them as fabricated. Releases
// that may never have shipped are the last thing to invent.
const fmt = (v) => formatDate(v, "");

// `GET /updates` has no query params at all — it always returns every published
// entry in one response — so search and paging are both done client-side here,
// against the list already in memory. Reasonable for a changelog (tens of
// entries, not thousands); a server-side page/search param would be the right
// move only once the list actually grows past what one response should carry.
const PAGE_SIZE = 8;

export default function WhatsNew() {
  const { data, loading, demo, error } = useApiData(getUpdates);
  // Memoised: `(data && data.entries) || []` builds a NEW array reference every
  // render, which made the `filtered` memo below recompute on every keystroke
  // anywhere on the page, not just when the search query or the data changed.
  const entries = useMemo(() => (data && data.entries) || [], [data]);

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.title, e.body, e.category, e.version].some((f) => String(f || "").toLowerCase().includes(q))
    );
  }, [entries, query]);

  // A search that narrows the list, or the list itself changing size (a fresh
  // load), can both leave `page` pointing past the end — reset rather than
  // render an empty page with working-looking pager buttons above it.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [query]);
  useEffect(() => { setPage((p) => Math.min(p, totalPages)); }, [totalPages]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // "No updates published yet" is a claim about the changelog; a failed request
  // supports no such claim.
  const failure = error || (demo ? "Couldn't reach the server — updates are unavailable." : null);

  return (
    <>
      <PageHeader title="What's New" subtitle="The latest features, improvements and fixes." />
      {failure && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-2.5 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertTriangle size={15} className="shrink-0" /> {failure}
        </div>
      )}

      {/* Search only once there's something to search — an empty or single-entry
          changelog has no use for a search box, and showing one anyway invites
          typing into a field that can only ever narrow one thing down to itself. */}
      {!loading && !failure && entries.length > 1 && (
        <div className="relative mb-5 max-w-sm">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search updates…"
            aria-label="Search updates"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-4 focus:ring-brand-500/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : entries.length === 0 ? (
        !failure && (
          <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-400 dark:border-white/10">
            <Sparkles size={22} className="mx-auto mb-2 opacity-50" />No updates published yet.
          </div>
        )
      ) : filtered.length === 0 ? (
        // Distinct from "no updates published yet" — there ARE updates, this
        // search just doesn't match any of them. Conflating the two messages
        // would read as "the changelog is empty" when it plainly isn't.
        <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-400 dark:border-white/10">
          <Search size={22} className="mx-auto mb-2 opacity-50" />
          No updates match &quot;{query}&quot;.
        </div>
      ) : (
        <>
          <div className="relative space-y-6 before:absolute before:left-[7px] before:top-2 before:h-full before:w-px before:bg-slate-200 dark:before:bg-white/10">
            {/* min-w-0 all the way down the chain, not only on the card: the card
                cannot stay inside a row that itself refuses to shrink, so the
                constraint has to hold at every level between the page and the
                text. max-w-full is the belt to that braces — it caps the card at
                its column even if a descendant demands more. */}
            {paged.map((e) => (
              <div key={e.id} className="relative min-w-0 max-w-full pl-8">
                <span className="absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-brand-500 dark:border-ink-950" />
                {/* Deliberately NO overflow-hidden on the card itself. The
                    wrapping is fixed at the text (overflow-wrap) and at the box
                    (min-w-0); clipping here would only paper over anything that
                    still escaped, and the next long URL would go unnoticed. */}
                <div className="card min-w-0 max-w-full p-5">
                  {/* flex-wrap, because the metadata row holds a category pill, a
                      version string and a date — on a phone those do not fit on one
                      line, and `ml-auto` alone would push the date off the edge. */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={categoryTone(e.category)}>{e.category || "update"}</Badge>
                    {e.version && <span className="text-xs font-medium text-slate-400">{e.version}</span>}
                    {/* Shown only when it is actually targeted: tagging every entry
                        "Everyone" is noise on a feed where that is the norm. */}
                    {e.audience && e.audience !== "all" && (
                      <Badge className="bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300">
                        For {audienceLabel(e.audience).toLowerCase()}
                      </Badge>
                    )}
                    <span className="ml-auto text-xs text-slate-400">{fmt(e.published_at)}</span>
                  </div>
                  {/* The title is author-typed and can be one unbroken token (a
                      URL, a build id), so it wraps rather than widening the card.
                      NOT truncate — a headline must stay readable in full. */}
                  <h3 className="mt-2 min-w-0 max-w-full break-words [overflow-wrap:anywhere] text-base font-semibold text-slate-800 dark:text-white">{e.title}</h3>
                  {/* The body goes through the shared renderer, which bounds the
                      card's height and offers Read more when there is more to
                      see. Nothing is truncated — the full text is one click
                      away, and what is stored is untouched. */}
                  <ExpandableText
                    text={e.body}
                    expandable
                    className="mt-1 text-sm text-slate-600 dark:text-slate-300"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Same pager chrome as DataTable's, so this doesn't read as a second,
              differently-styled control elsewhere in the app — only shown once
              there's more than one page to move between. */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-400">
                {filtered.length.toLocaleString()} update{filtered.length === 1 ? "" : "s"}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                  className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                >
                  <ChevronLeft size={15} />
                </button>
                {pageList(page, totalPages).map((p, i) =>
                  p === "…" ? (
                    <span key={`gap-${i}`} className="px-1.5 text-slate-400">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      aria-current={p === page ? "page" : undefined}
                      className={cn(
                        "h-8 min-w-8 rounded-lg px-2 text-xs font-semibold transition-colors",
                        p === page
                          ? "bg-brand-600 text-white shadow-soft"
                          : "border border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                      )}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  aria-label="Next page"
                  className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
